import customerApiClient from '../lib/apiClient';
import { buildProductImageUrl } from './adminProductService';

const BANNER_IMAGE_BASE_URL =
  process.env.REACT_APP_BANNER_IMAGE_BASE_URL ||
  'http://localhost/papad/public/uploads/banners/';

const HOME_GUEST_TOKEN_KEY = 'guest_token';

function appendIfPresent(formData, key, value) {
  if (value !== undefined && value !== null && String(value).trim() !== '') {
    formData.append(key, String(value).trim());
  }
}

function ensureGuestToken() {
  const existingToken = localStorage.getItem(HOME_GUEST_TOKEN_KEY);
  if (existingToken) {
    return existingToken;
  }

  const generatedToken = `guest_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;

  localStorage.setItem(HOME_GUEST_TOKEN_KEY, generatedToken);
  return generatedToken;
}

function buildBannerImageUrl(imageName) {
  if (!imageName) {
    return '';
  }

  if (/^https?:\/\//i.test(imageName)) {
    return imageName;
  }

  return `${BANNER_IMAGE_BASE_URL}${imageName}`;
}

function mapCategory(item) {
  return {
    category_id: item.category_id ?? item.id ?? item.categoryId ?? '',
    name: item.name ?? '',
    product_count: item.product_count ?? item.productCount ?? 0,
  };
}

function mapProduct(item) {
  const productId = item.product_id ?? item.id ?? item.productId ?? '';

  return {
    id: productId,
    product_id: productId,
    name: item.name ?? '',
    description: item.description ?? '',
    image: buildProductImageUrl(item.image),
    size: item.weight ?? item.size ?? item.variant_name ?? '',
    variant_id: item.variant_id ?? item.variantId ?? '',
    variant_name: item.variant_name ?? item.variantName ?? '',
    price: item.price ?? '',
    mrp: item.mrp ?? '',
    stock: item.stock ?? '',
    is_best_selling: item.is_best_selling ?? item.isBestSelling ?? 0,
  };
}

function mapBanner(item, index) {
  return {
    banner_id: item.banner_id ?? item.id ?? item.bannerId ?? index + 1,
    title: item.title ?? item.name ?? `Banner ${index + 1}`,
    subtitle: item.subtitle ?? item.description ?? item.offer_text ?? '',
    image: buildBannerImageUrl(item.image),
  };
}

function extractItems(payload, key, mapper) {
  const rawItems = Array.isArray(payload?.[key]) ? payload[key] : [];
  return rawItems.map(mapper);
}

export async function fetchHomeDashboard({ userId, guestToken } = {}) {
  const formData = new FormData();
  const resolvedGuestToken = guestToken || ensureGuestToken();

  if (userId) {
    appendIfPresent(formData, 'user_id', userId);
  } else {
    appendIfPresent(formData, 'guest_token', resolvedGuestToken);
  }

  const response = await customerApiClient.post('/home/dashboard', formData);
  const payload = response.data?.data || {};

  return {
    raw: response.data,
    guestToken: resolvedGuestToken,
    banners: extractItems(payload, 'banners', mapBanner),
    categories: extractItems(payload, 'categories', mapCategory),
    bestSelling: extractItems(payload, 'best_selling', mapProduct),
    newArrivals: extractItems(payload, 'new_arrivals', mapProduct),
    recommended: extractItems(payload, 'recommended', mapProduct),
    trending: extractItems(payload, 'trending', mapProduct),
  };
}

export async function fetchProductsByCategory({
  categoryId,
  userId,
  guestToken,
  page = 1,
} = {}) {
  const formData = new FormData();
  const resolvedGuestToken = guestToken || ensureGuestToken();

  appendIfPresent(formData, 'category_id', categoryId);
  appendIfPresent(formData, 'page', page);

  if (userId) {
    appendIfPresent(formData, 'user_id', userId);
  } else {
    appendIfPresent(formData, 'guest_token', resolvedGuestToken);
  }

  const response = await customerApiClient.post('/products/by-category', formData);
  const payload = Array.isArray(response.data?.data) ? response.data.data : [];

  return {
    raw: response.data,
    guestToken: resolvedGuestToken,
    items: payload.map(mapProduct),
  };
}

export async function fetchProductDetails({ productId, userId, guestToken } = {}) {
  const formData = new FormData();
  const resolvedGuestToken = guestToken || ensureGuestToken();

  appendIfPresent(formData, 'product_id', productId);

  if (userId) {
    appendIfPresent(formData, 'user_id', userId);
  } else {
    appendIfPresent(formData, 'guest_token', resolvedGuestToken);
  }

  const response = await customerApiClient.post('/products/details', formData);
  const payload = response.data?.data || {};
  const product = payload.product || {};
  const images = Array.isArray(payload.images) ? payload.images : [];
  const variants = Array.isArray(payload.variants) ? payload.variants : [];

  return {
    raw: response.data,
    guestToken: resolvedGuestToken,
    product: {
      product_id: product.product_id ?? product.id ?? '',
      name: product.name ?? '',
      description: product.description ?? '',
      image: buildProductImageUrl(product.image),
    },
    images: images.map((item) => ({
      image: buildProductImageUrl(item.image),
    })),
    variants: variants.map((item) => ({
      variant_id: item.variant_id ?? item.id ?? '',
      variant_name: item.variant_name ?? '',
      price: item.price ?? '',
      mrp: item.mrp ?? '',
      stock: item.stock ?? '',
    })),
  };
}
